import Button from '@rio-cloud/rio-uikit/Button';
import Card from '@rio-cloud/rio-uikit/Card';
import Multiselect from '@rio-cloud/rio-uikit/Multiselect';
import Pills, { PillsItem } from '@rio-cloud/rio-uikit/Pills';
import Switch from '@rio-cloud/rio-uikit/Switch';
import { TableColumnsDropdown } from '@rio-cloud/rio-uikit/Table';
import TableSearch from '@rio-cloud/rio-uikit/TableSearch';
import TableToolbar, { TableToolbarColumn } from '@rio-cloud/rio-uikit/TableToolbar';
import ToggleButton from '@rio-cloud/rio-uikit/ToggleButton';
import { useEffect, useId, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { TermedLevel } from '../../common/termedLevel/types';
import { getLocalizedPeriod } from '../../common/utils/productLabelUtil';
import { BookingOverviewDateRange } from './BookingOverviewDateRange';
import {
    type BookingOverviewColumn,
    type BookingOverviewGrouping,
    bookingOverviewColumns,
} from './bookingOverviewColumns';
import {
    type BookingOverviewFilters,
    getBookingOverviewPeriods,
    getBookingOverviewResourceKey,
} from './bookingOverviewFilters';
import type { BookingOverviewStatus } from './bookingOverviewStatus';

type Props = {
    contracts: TermedLevel[];
    filters: BookingOverviewFilters;
    onChange: (filters: BookingOverviewFilters) => void;
    onClear: () => void;
    grouping: BookingOverviewGrouping;
    onGroupingChange: (grouping: BookingOverviewGrouping) => void;
    search: string;
    onSearchChange: (search: string) => void;
    visibleColumns: BookingOverviewColumn[];
    columnOrder: BookingOverviewColumn[];
    onVisibleColumnsChange: (columns: BookingOverviewColumn[]) => void;
    onColumnOrderChange: (columns: string[]) => void;
    onResetColumns: () => void;
};

export function BookingOverviewToolbar({
    contracts,
    filters,
    onChange,
    onClear,
    grouping,
    onGroupingChange,
    search,
    onSearchChange,
    visibleColumns,
    columnOrder,
    onVisibleColumnsChange,
    onColumnOrderChange,
    onResetColumns,
}: Props) {
    const intl = useIntl();
    const id = useId();
    const statuses: BookingOverviewStatus[] = ['ACTIVE', 'PENDING', 'EXPIRING', 'UNASSIGNED'];
    const hasFilters =
        filters.resources.length > 0 ||
        filters.periods.length > 0 ||
        filters.statuses.length > 0 ||
        filters.freeOnly ||
        filters.withoutRenewal ||
        Boolean(filters.endsFrom || filters.endsTo);
    const activeFiltersKey = hasFilters ? JSON.stringify(filters) : '';
    const [showFilters, setShowFilters] = useState(hasFilters);

    useEffect(() => {
        if (activeFiltersKey) {
            setShowFilters(true);
        }
    }, [activeFiltersKey]);

    const filterLabel = intl.formatMessage({ id: 'intl-msg:marketplace.bookings.overview.toolbar.filters' });
    const searchLabel = intl.formatMessage({
        id: 'intl-msg:marketplace.bookings.overview.toolbar.searchVehicles',
    });

    const resourceOptions = [
        ...new Map(
            contracts.map(contract => [
                getBookingOverviewResourceKey(contract),
                { id: getBookingOverviewResourceKey(contract), label: contract.resourceName || contract.resourceId },
            ])
        ).values(),
    ].sort((a, b) => a.label.localeCompare(b.label));
    const selectors = [
        {
            key: 'vehicle',
            icon: 'uikit-icon icon-truck',
            label: 'vehicleFilter',
            placeholder: 'filterVehicles',
            options: resourceOptions,
            value: filters.resources,
            onChange: (resources: string[]) => onChange({ ...filters, resources }),
        },
        {
            key: 'period',
            icon: 'uikit-icon icon-calendar',
            label: 'periodFilter',
            placeholder: 'filterPeriods',
            options: getBookingOverviewPeriods(contracts).map(period => ({
                id: period.id,
                label: getLocalizedPeriod(period, intl),
            })),
            value: filters.periods,
            onChange: (periods: string[]) => onChange({ ...filters, periods }),
        },
        {
            key: 'status',
            icon: 'rioglyph rioglyph-sphere uikit-icon icon-sphere',
            label: 'statusFilter',
            placeholder: 'filterStatus',
            options: statuses.map(status => ({
                id: status,
                label: intl.formatMessage({
                    id: `intl-msg:marketplace.bookings.overview.status.${status.toLowerCase()}`,
                }),
            })),
            value: filters.statuses,
            onChange: (selected: string[]) =>
                onChange({ ...filters, statuses: statuses.filter(status => selected.includes(status)) }),
        },
    ];
    return (
        <>
            <TableToolbar>
                <TableToolbarColumn>
                    <Pills variant='filled-condensed' className='shadow-subtle'>
                        {(['services', 'resources'] as const).map(view => (
                            <PillsItem
                                key={view}
                                active={grouping === view}
                                role='button'
                                tabIndex={0}
                                aria-pressed={grouping === view}
                                onClick={() => onGroupingChange(view)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        onGroupingChange(view);
                                    }
                                }}
                            >
                                <FormattedMessage
                                    id={
                                        view === 'services'
                                            ? 'intl-msg:marketplace.myServices'
                                            : 'intl-msg:marketplace.bookings.overview.toolbar.vehicles'
                                    }
                                />
                            </PillsItem>
                        ))}
                    </Pills>
                </TableToolbarColumn>
                <TableToolbarColumn horizontalAlign='right'>
                    <ToggleButton
                        bsStyle='primary'
                        variant='outline'
                        iconName={hasFilters ? 'icon-filter-active' : 'icon-filter'}
                        active={showFilters}
                        onClick={setShowFilters}
                        aria-expanded={showFilters}
                        aria-controls={`${id}-filters`}
                    >
                        {filterLabel}
                    </ToggleButton>
                </TableToolbarColumn>
                <TableToolbarColumn horizontalAlign='right'>
                    <BookingOverviewDateRange filters={filters} onChange={onChange} />
                </TableToolbarColumn>
                <TableToolbarColumn horizontalAlign='right'>
                    <TableSearch
                        value={search}
                        onChange={onSearchChange}
                        placeholder={searchLabel}
                        aria-label={searchLabel}
                    />
                </TableToolbarColumn>
                <TableToolbarColumn horizontalAlign='right'>
                    <TableColumnsDropdown
                        label={<FormattedMessage id='intl-msg:marketplace.bookings.overview.toolbar.columns' />}
                        toggleClassName='btn-primary btn-outline'
                        columns={columnOrder
                            .flatMap(id => bookingOverviewColumns.filter(column => column.id === id))
                            .map(column => ({
                                key: column.id,
                                label: (
                                    <FormattedMessage
                                        id={
                                            column.id === 'resourceName' && grouping === 'resources'
                                                ? 'intl-msg:marketplace.history.header.service'
                                                : column.labelId
                                        }
                                    />
                                ),
                                visible: visibleColumns.includes(column.id),
                                disabled: column.id === 'resourceName',
                            }))}
                        onColumnOrderChange={onColumnOrderChange}
                        onColumnVisibilityChange={(key, visible) =>
                            onVisibleColumnsChange(
                                columnOrder.filter(
                                    column =>
                                        column === 'resourceName' ||
                                        (column === key ? visible : visibleColumns.includes(column))
                                )
                            )
                        }
                        showResetButton
                        resetLabel={
                            <FormattedMessage id='intl-msg:marketplace.bookings.overview.toolbar.resetColumns' />
                        }
                        onReset={onResetColumns}
                    />
                </TableToolbarColumn>
            </TableToolbar>
            {showFilters && (
                <section id={`${id}-filters`} aria-label={filterLabel}>
                    <Card className='margin-bottom-20'>
                        <div className='display-grid grid-cols-1 grid-cols-2-sm grid-cols-3-md gap-15'>
                            {selectors.map(selector => (
                                <div key={selector.key}>
                                    <label htmlFor={`${id}-${selector.key}`} className='text-light text-size-12'>
                                        <span className={`${selector.icon} margin-right-5`} aria-hidden={true} />
                                        <FormattedMessage
                                            id={`intl-msg:marketplace.bookings.overview.toolbar.${selector.label}`}
                                        />
                                    </label>
                                    <Multiselect
                                        id={`${id}-${selector.key}`}
                                        options={selector.options}
                                        value={selector.value}
                                        onChange={selector.onChange}
                                        placeholder={intl.formatMessage({
                                            id: `intl-msg:marketplace.bookings.overview.toolbar.${selector.placeholder}`,
                                        })}
                                        useFilter
                                        useClear
                                    />
                                </div>
                            ))}
                        </div>
                        <div className='display-flex flex-wrap justify-content-between gap-15 margin-top-15'>
                            <div className='display-flex flex-wrap gap-15'>
                                <Switch
                                    keyName={`${id}-free`}
                                    checked={filters.freeOnly}
                                    onChange={freeOnly => onChange({ ...filters, freeOnly })}
                                >
                                    <FormattedMessage id='intl-msg:marketplace.bookings.overview.filters.free' />
                                </Switch>
                                <Switch
                                    keyName={`${id}-renewal`}
                                    checked={filters.withoutRenewal}
                                    onChange={withoutRenewal => onChange({ ...filters, withoutRenewal })}
                                >
                                    <FormattedMessage id='intl-msg:marketplace.bookings.overview.filters.withoutRenewal' />
                                </Switch>
                            </div>
                            <Button onClick={onClear}>
                                <FormattedMessage id='intl-msg:marketplace.clearAllFilters' />
                            </Button>
                        </div>
                    </Card>
                </section>
            )}
        </>
    );
}
